import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { selectPipelineDefinition } from "@/marketing/lib/campaignPipeline";
import { CAMPAIGN_PIPELINE_QUERIES } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/queries";
import {
  STANDARD_PIPELINE_TYPE,
  type CampaignPipelineDefinition,
} from "@/marketing/models/campaignPipeline";

export interface CampaignPipelineDefinitionState {
  definition: CampaignPipelineDefinition | undefined;
  isLoading: boolean;
  errorMessage: string | null;
}

export function useCampaignPipelineDefinition(
  pipelineType?: string,
): CampaignPipelineDefinitionState {
  const query = useQuery(CAMPAIGN_PIPELINE_QUERIES.definitions());

  return useMemo(() => {
    if (query.isError) {
      return {
        definition: undefined,
        isLoading: false,
        errorMessage:
          query.error instanceof Error
            ? query.error.message
            : "Failed to load pipeline definitions.",
      };
    }
    if (!query.data) {
      return { definition: undefined, isLoading: true, errorMessage: null };
    }
    const definition = selectPipelineDefinition(query.data, pipelineType);
    return {
      definition,
      isLoading: false,
      errorMessage: definition
        ? null
        : `No pipeline definition for "${pipelineType ?? STANDARD_PIPELINE_TYPE}".`,
    };
  }, [query.isError, query.error, query.data, pipelineType]);
}
